import type { BrowserWindow } from '@wix/thunderbolt-symbols'

export type MpaSessionCache<T> = {
	/**
	 * Reads the cached value. Returns `null` when nothing is stored or the
	 * stored value can't be parsed.
	 */
	read: () => T | null
	write: (value: T) => void
	clear: () => void
}

/**
 * Creates an MPA-scoped sessionStorage cache.
 *
 * A hard page reload resets the cache: any value carried over from a previous
 * (pre-reload) visit is dropped on creation, so a reload behaves like a fresh
 * visit and a stale value can't resurface after a later same-session navigation
 * (e.g. reload -> navigate away -> navigate back). This matches the SPA
 * experience across MPA navigations.
 */
export const createMpaSessionCache = <T>({
	browserWindow,
	storageKey,
}: {
	browserWindow?: BrowserWindow | Window
	storageKey: string
}): MpaSessionCache<T> => {
	const clear = (): void => {
		try {
			browserWindow?.sessionStorage?.removeItem?.(storageKey)
		} catch {}
	}

	const navEntries = browserWindow?.performance?.getEntriesByType?.('navigation') as unknown as
		| Array<PerformanceNavigationTiming>
		| undefined
	const isHardReload = !!navEntries?.some((entry) => entry.type === 'reload')
	if (isHardReload) {
		clear()
	}

	return {
		read: () => {
			try {
				const cached = browserWindow?.sessionStorage?.getItem?.(storageKey)
				return cached ? (JSON.parse(cached) as T) : null
			} catch {
				return null
			}
		},
		write: (value) => {
			try {
				browserWindow?.sessionStorage?.setItem?.(storageKey, JSON.stringify(value))
			} catch {}
		},
		clear,
	}
}
